'use client'

import { Unsaved } from '@carbon/icons-react'
import { useDebouncedCallback } from '@mantine/hooks'
import Loading from 'app/_components/Loading'
import { Tooltip } from 'app/_components/Tooltip'
import Saved from 'app/(main)/_icons/SavedIcon'
import { cx } from 'lib/cx'
import type { ServerActionResponse } from 'lib/serverActionsResponse'
import { useState } from 'react'

const StatusConfig = {
  idle: null,
  saving: [Loading, 'Änderungen werden gespeichert'],
  saved: [Saved, 'Alle Änderungen wurden als Entwurf gespeichert.'],
  error: [Unsaved, 'Fehler beim Speichern der Änderungen.'],
} as const

export type Status = keyof typeof StatusConfig

export function AutoSaveStatus({
  status = 'idle',
  className,
}: {
  status: Status
  className?: string
}) {
  if (status === 'idle') return

  const [Icon, tooltip] = StatusConfig[status]
  const isError = status === 'error'

  return (
    <Tooltip content={tooltip} open={isError ? true : undefined}>
      <div className={cx('p-[15px]', isError && 'text-text-error', className)}>
        <Icon className="h-4 w-4" />
      </div>
    </Tooltip>
  )
}

export function fromServerActionResponse(res: ServerActionResponse): Status {
  switch (res.status) {
    case 'success':
      return 'saved'
    case 'error':
      console.error(res.message)
      return 'error'
    case 'pending':
      return 'saving'
    default:
      return 'idle'
  }
}

export function useAutoSaveStatus(
  options: { initialValue?: Status; idleAfterSaved?: boolean } = {},
): [Status, (res: Status) => void] {
  const { initialValue = 'idle', idleAfterSaved = true } = options
  const [state, setState] = useState(initialValue)

  const debouncedSetIdle = useDebouncedCallback(() => setState('idle'), 5000)

  return [
    state,
    (value: Status) => {
      debouncedSetIdle.cancel()
      setState(value)

      if (value === 'saved' && idleAfterSaved) debouncedSetIdle()
    },
  ]
}
